import { useState, useEffect } from 'react';

const AddLeadsModal = ({ isOpen, onClose, mover, onUpdateLeads }) => {
  const [totalLeads, setTotalLeads] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen && mover) {
      // Set current total leads value when modal opens
      setTotalLeads(mover.total_leads ?? '');
      setError('');
    }
  }, [isOpen, mover]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validate input
    const leadsNumber = parseInt(totalLeads);
    if (isNaN(leadsNumber) || leadsNumber < 0) {
      setError('Please enter a valid number (0 or greater)');
      return;
    }

    setLoading(true);
    try {
      await onUpdateLeads(mover.id, leadsNumber);
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to update leads');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setTotalLeads('');
      setError('');
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-800">Update Total Leads</h2>
          <button
            onClick={handleClose}
            disabled={loading}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition disabled:opacity-50"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {mover && (
            <div className="mb-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0 bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center text-white text-lg font-bold">
                  {mover.name?.charAt(0).toUpperCase()}
                </div>
                <div>
                  <h3 className="font-semibold text-gray-800">{mover.name}</h3>
                  <p className="text-sm text-gray-500">{mover.location}</p>
                </div>
              </div>

              {/* Current Stats */}
              <div className="bg-gray-50 rounded-lg p-4 grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-600 mb-1">Total Leads</p>
                  <p className="text-lg font-bold text-gray-800">
                    {mover.total_leads || 0}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-600 mb-1">Consumed Leads</p>
                  <p className="text-lg font-bold text-gray-800">
                    {mover.consumed_leads || 0}
                  </p>
                </div>
                <div className="col-span-2">
                  <p className="text-xs text-gray-600 mb-1">Remaining Leads</p>
                  <p className="text-lg font-bold text-green-600">
                    {(mover.total_leads || 0) - (mover.consumed_leads || 0)}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit}>
            <div className="mb-6">
              <label htmlFor="totalLeads" className="block text-sm font-medium text-gray-700 mb-2">
                New Total Leads
              </label>
              <input
                type="number"
                id="totalLeads"
                value={totalLeads}
                onChange={(e) => setTotalLeads(e.target.value)}
                min="0"
                step="1"
                placeholder="Enter number of leads"
                className="w-full px-4 py-2.5 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-gray-800"
                disabled={loading}
                required
              />
              <p className="mt-2 text-xs text-gray-500">
                Set total leads capacity for this mover. Enter 0 for no leads.
              </p>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                <svg className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3">
              <button
                type="button"
                onClick={handleClose}
                disabled={loading}
                className="flex-1 px-4 py-2.5 bg-white border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 transition disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 px-4 py-2.5 bg-orange-600 text-white rounded-lg font-medium hover:bg-orange-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Updating...
                  </>
                ) : (
                  'Update Leads'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddLeadsModal;
